import React, { useState, useEffect } from 'react';
import '../styles/QuizView.scss';
import { copyToClipboard } from '../utils/copyToClipboard';
import { FaCheck, FaCopy } from 'react-icons/fa';
import { track } from '@vercel/analytics';

function QuizView({ 
  API_BASE_URL, 
  authToken, 
  quizId, 
  attemptId,
  onBack, 
  onComplete 
}) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [pollingTaskId, setPollingTaskId] = useState(null);
  const [initialAttemptIdToLoad, setInitialAttemptIdToLoad] = useState(attemptId);
  const [copiedShareId, setCopiedShareId] = useState(false);

  // Fetch quiz data
  useEffect(() => {
    async function fetchQuiz() {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/quiz/${quizId}`,
          {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch quiz: ${response.status}`);
        }

        const data = await response.json();
        setQuiz(data);
        // Debug: Log the quiz data to see the structure
        console.log('Quiz data:', data);
        console.log('First question:', data.questions[0]);
        // Initialize answers array with empty strings
        setAnswers(new Array(data.questions.length).fill(''));
        
        // Track quiz loading
        track('quiz_loaded', {
          quiz_id: quizId,
          quiz_title: data.title,
          question_count: data.questions.length,
          subject: data.subject,
          has_attempt_id: !!attemptId
        });
      } catch (err) {
        console.error('Error fetching quiz:', err);
        setError(err.message);
        
        // Track quiz loading error
        track('quiz_load_error', {
          quiz_id: quizId,
          error: err.message
        });
      } finally {
        setLoading(false);
      }
    }

    fetchQuiz();
  }, [API_BASE_URL, authToken, quizId, attemptId]);

  // useEffect to fetch results if an initialAttemptIdToLoad is provided
  useEffect(() => {
    const fetchAttemptResults = async (idToFetch) => {
      setSubmitting(true); // Show loading/submitting state while fetching results
      setError(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/quiz/attempt/${idToFetch}`,
          {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `Failed to fetch attempt results: ${response.status}` }));
          throw new Error(errorData.error || `Failed to fetch attempt results: ${response.status}`);
        }
        const data = await response.json();
        if (data.status === 'completed') {
          setResults(data);
          setSubmitted(true); // Ensure we show the results view
          // If student's answers for this attempt are also in 'data', set them.
          // Assuming 'data.answers' is an array of strings like the 'answers' state.
          if (data.answers && Array.isArray(data.answers)) {
            setAnswers(data.answers);
          }
        } else if (data.status === 'in_progress') {
          setError("This quiz attempt is still being graded. Please check back later.");
        } else {
          throw new Error(data.error || 'Failed to load quiz results.');
        }
      } catch (err) {
        console.error('Error fetching initial attempt results:', err);
        setError(err.message);
      } finally {
        setSubmitting(false);
      }
    };

    if (initialAttemptIdToLoad && !results && !pollingTaskId) { // Only if initialAttemptId is present, results aren't loaded, and not already polling for a new submission
      fetchAttemptResults(initialAttemptIdToLoad);
    }
    // Clear initialAttemptIdToLoad after attempting to load, to prevent re-fetch on other state changes unless component re-mounts with new attemptId prop
    // However, this might be too aggressive if quiz data loading takes time. Better to rely on !results check.
  }, [API_BASE_URL, authToken, initialAttemptIdToLoad, results, pollingTaskId]);

  // Poll for quiz grading results if a NEW quiz was just submitted (pollingTaskId is set)
  useEffect(() => {
    if (!pollingTaskId) return; // Only poll if a new submission created a pollingTaskId

    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/quiz/attempt/${pollingTaskId}`,
          {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to check status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.status === 'completed') {
          // We have results
          setResults(data);
          setPollingTaskId(null);
          setSubmitting(false);
          setSubmitted(true); // Ensure this is set for newly submitted quizzes too
          // If student's answers for this attempt are also in 'data', set them.
          if (data.answers && Array.isArray(data.answers)) {
            setAnswers(data.answers);
          }
          
          // Track quiz completion and results
          track('quiz_results_received', {
            quiz_id: quizId,
            attempt_id: pollingTaskId,
            score: data.score,
            max_score: data.max_score,
            percentage: data.percentage,
            subject: quiz?.subject
          });
        } else if (data.status === 'in_progress') {
          // Still processing, poll again after delay
          setTimeout(checkStatus, 2000);
        } else {
          // Error state
          throw new Error('Grading failed');
        }
      } catch (err) {
        console.error('Error checking quiz status:', err);
        setError(err.message);
        setSubmitting(false);
        setPollingTaskId(null);
      }
    };

    // Start polling
    checkStatus();
    
    // Cleanup
    return () => {
      setPollingTaskId(null);
    };
  }, [API_BASE_URL, authToken, pollingTaskId]);

  // Handle answer change
  const handleAnswerChange = (e) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = e.target.value;
    setAnswers(newAnswers);
  };

  // Move to next question
  const handleNextQuestion = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  // Move to previous question
  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Submit quiz
  const handleSubmitQuiz = async () => {
    try {
      setSubmitting(true);
      
      // Track quiz submission attempt
      const answeredQuestions = answers.filter(answer => answer.trim().length > 0).length;
      track('quiz_submit_started', {
        quiz_id: quizId,
        total_questions: quiz.questions.length,
        answered_questions: answeredQuestions,
        completion_rate: (answeredQuestions / quiz.questions.length * 100).toFixed(1),
        subject: quiz?.subject
      });
      
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/${quizId}/attempt`,
        {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ answers })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to submit quiz: ${response.status}`);
      }

      const data = await response.json();
      setSubmitted(true);
      setPollingTaskId(data.attempt_id);
      
      // Track successful quiz submission
      track('quiz_submit_success', {
        quiz_id: quizId,
        attempt_id: data.attempt_id,
        subject: quiz?.subject
      });
    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError(err.message);
      setSubmitting(false);
      
      // Track quiz submission error
      track('quiz_submit_error', {
        quiz_id: quizId,
        error: err.message,
        subject: quiz?.subject
      });
    }
  };

  // Navigate directly to a question
  const jumpToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  const handleCopyShareId = async (id) => {
    const success = await copyToClipboard(id);
    if (success) {
      setCopiedShareId(true);
      setTimeout(() => setCopiedShareId(false), 2000); // Reset after 2 seconds
      
      // Track share ID copy
      track('quiz_share_id_copied', {
        quiz_id: quizId,
        share_id: id,
        subject: quiz?.subject
      });
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="quiz-view loading">
        <div className="loading-spinner"></div>
        <p>Loading quiz...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="quiz-view error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={onBack} className="back-button">Go Back</button>
      </div>
    );
  }

  // Render quiz results
  if (results) {
    return (
      <div className="quiz-view results">
        <div className="quiz-header-info">
          <h2>Quiz Results</h2>
          {quiz && quiz.share_id && (
            <div className="share-id-display">
              <span>Share ID: {quiz.share_id}</span>
              <button onClick={() => handleCopyShareId(quiz.share_id)} className="copy-share-id-button">
                {copiedShareId ? <FaCheck /> : <FaCopy />}
                {copiedShareId ? ' Copied!' : ' Copy ID'}
              </button>
            </div>
          )}
        </div>
        <div className="quiz-score">
          <h3>
            Your Score: {results.score} / {results.max_score} ({results.percentage}%)
          </h3>
        </div>
        
        <div className="quiz-feedback">
          <h3>Question Feedback</h3>
          {results.feedback.question_results.map((result, index) => (
            <div key={index} className="question-feedback">
              <div className="question-text">
                <strong>Question {index + 1}:</strong> {quiz.questions[index].question}
              </div>
              <div className="answer-text">
                <strong>Your Answer:</strong> {answers[index] || '(No answer provided)'}
              </div>
              <div className="correct-answer">
                <strong>Correct Answer:</strong> {result.correct_answer}
              </div>
              <div className="feedback-text">
                <strong>Feedback:</strong> {result.feedback}
              </div>
              <div className="question-score">
                <strong>Score:</strong> {result.score} / 1
              </div>
            </div>
          ))}
        </div>
        
        <div className="action-buttons">
          <button onClick={() => {
            track('quiz_results_back_to_quizzes', {
              quiz_id: quizId,
              score: results.score,
              max_score: results.max_score,
              percentage: results.percentage,
              subject: quiz?.subject
            });
            onBack();
          }} className="back-button">Back to Quizzes</button>
          <button onClick={() => {
            track('quiz_results_take_another', {
              quiz_id: quizId,
              score: results.score,
              max_score: results.max_score,
              percentage: results.percentage,
              subject: quiz?.subject
            });
            onComplete();
          }} className="new-quiz-button">Take Another Quiz</button>
        </div>
      </div>
    );
  }

  // Render quiz taking interface
  if (quiz) {
    const currentQ = quiz.questions[currentQuestion];
    
    return (
      <div className="quiz-view">
        <div className="quiz-header">
          <h2>{quiz.title}</h2>
          {quiz.share_id && (
            <div className="share-id-display quiz-taking-share-id">
              <span>Share ID: {quiz.share_id}</span>
              <button onClick={() => handleCopyShareId(quiz.share_id)} className="copy-share-id-button">
                {copiedShareId ? <FaCheck /> : <FaCopy />}
                {copiedShareId ? ' Copied!' : ' Copy ID'}
              </button>
            </div>
          )}
          <div className="quiz-progress">
            Question {currentQuestion + 1} of {quiz.questions.length}
          </div>
        </div>
        
        <div className="quiz-navigation">
          {/* Question indicator buttons */}
          <div className="question-indicators">
            {quiz.questions.map((_, index) => (
              <button 
                key={index} 
                className={`indicator ${index === currentQuestion ? 'active' : ''} ${answers[index] ? 'answered' : ''}`}
                onClick={() => jumpToQuestion(index)}
                disabled={submitting}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button onClick={onBack} className="exit-quiz-button" disabled={submitting}>Exit Quiz</button>
        </div>
        
        <div className="quiz-question">
          <h3 className="question-text">{currentQ.question}</h3>
          
          <div className="answer-input">
            <textarea 
              value={answers[currentQuestion]} 
              onChange={handleAnswerChange}
              placeholder="Enter your answer here..."
              rows={6}
              disabled={submitted}
            />
          </div>
        </div>
        
        <div className="quiz-actions">
          <button 
            onClick={handlePrevQuestion} 
            disabled={currentQuestion === 0 || submitted || submitting}
            className="nav-button prev"
          >
            Previous
          </button>
          
          {currentQuestion < quiz.questions.length - 1 ? (
            <button 
              onClick={handleNextQuestion} 
              disabled={submitted || submitting}
              className="nav-button next"
            >
              Next
            </button>
          ) : (
            <button 
              onClick={handleSubmitQuiz} 
              disabled={submitted || submitting}
              className="submit-button"
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </button>
          )}
        </div>
        
        {submitted && !results && (
          <div className="submitting-overlay">
            <div className="loading-spinner"></div>
            <p>Grading your quiz...</p>
          </div>
        )}
      </div>
    );
  }

  // Fallback if quiz is not loaded (should be covered by loading/error states)
  return <div className="quiz-view"><p>Quiz data is not available.</p></div>;
}

export default QuizView; 